<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { number, object, string } from 'yup';

const { modulePackage, modulePackageTypes } = defineProps({
    modulePackage: {
        required: false,
        type: Object,
    },
    modulePackageTypes: {
        required: true,
        type: Array,
    },
});

const { route } = useRoute();

const form = reactive({
    name: modulePackage ? modulePackage.name : '',
    description: modulePackage ? modulePackage.description : '',
    type: modulePackage
        ? modulePackageTypes.find(
              (type) => type.value === modulePackage.type.value,
          )
        : modulePackageTypes[0],
    price: modulePackage ? modulePackage.price : null,
    minValue: modulePackage ? modulePackage.minValue : null,
    maxValue: modulePackage ? modulePackage.maxValue : null,
});

const isSubmitting = ref(false);

const validationSchema = object({
    name: string().required('Ce champ est obligatoire.'),
    description: string().nullable(),
    type: object().required('Ce champ est obligatoire.'),
    price: number().required('Ce champ est obligatoire.'),
    minValue: number().required('Ce champ est obligatoire.'),
    maxValue: number().required('Ce champ est obligatoire.'),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.modules.postModulePackage', {
                ...(modulePackage ? { modulePackage: modulePackage.id } : {}),
            }),
            {
                name: form.name,
                description: form.description,
                type: form.type.value,
                price: form.price,
                minValue: form.minValue,
                maxValue: form.maxValue,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.modules.modulePackages'),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.modules.modulePackages'),
            );
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <FormColumn>
            <FormContent>
                <FormGrid :columns="2">
                    <ListboxField
                        v-model="form.type"
                        field-id="type"
                        field-name="type"
                        label="Type"
                        :options="modulePackageTypes"
                        required
                    >
                        <template #buttonContent>
                            {{ form.type.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <InputField
                        v-model="form.name"
                        label="Nom"
                        field-name="name"
                        field-id="name"
                        type="text"
                        required
                    />
                </FormGrid>

                <FormGrid :columns="1">
                    <EditorField
                        v-model="form.description"
                        label="Description"
                        field-name="description"
                        field-id="description"
                        size="small"
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid>
                    <InputField
                        v-model="form.price"
                        label="Prix"
                        field-name="price"
                        field-id="price"
                        type="number"
                        min="0"
                        step="0.01"
                        required
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid>
                    <InputField
                        v-model="form.minValue"
                        label="Valeur min"
                        field-name="minValue"
                        field-id="minValue"
                        type="number"
                        min="0"
                        step="0.01"
                        required
                    />

                    <InputField
                        v-model="form.maxValue"
                        label="Valeur max"
                        field-name="maxValue"
                        field-id="maxValue"
                        type="number"
                        min="0"
                        step="0.01"
                        required
                    />
                </FormGrid>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="modulePackage"
                    :url="
                        route(
                            'front.projectManagement.modules.deleteModulePackage',
                            {
                                modulePackage: modulePackage.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.modules.modulePackages',
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
